// ============================================================
// predicateDamage - the target-predicate & wound-state damage
// family (v8.285.0). Pure: WHO is being hit, or WHERE the fight
// stands, decides the bonus - never the weapon alone.
//
// One table consulted from calcSingleHitDamage, so every rule
// here is forecast-correct in both probability engines for free.
// Bonuses SUM across rules; Witchbane and Purifier reach
// penetration as well as damage.
// ============================================================
import { getWSP } from './weaponSpecials';
import { condType } from './conditions';
import { sizeStepsAboveAverage } from './sizes';
import type { Target } from '../types';

export interface PredicateCtx {
  target: Target;
  /** Wounds REMAINING as this hit lands - the running count, not the sheet. */
  runningWounds: number;
  /** 0-based position of this hit inside the burst. */
  hitIndex: number;
}

/** Conditions that leave a target open to Cruel: prone, stunned, snared, pinned, blinded. */
const DISADVANTAGED = new Set(['P', 'S', 'Sn', 'Pn', 'B']);

interface PredicateRule {
  id: string;
  /** Witchbane and Purifier reach penetration as well as damage. */
  pen?: boolean;
  /** How many times the rule's X applies to this hit: 0 when its predicate fails. */
  factor: (ctx: PredicateCtx) => number;
}

const when = (holds: boolean): number => (holds ? 1 : 0);
const maxWoundsOf = (target: Target): number => target.wounds?.max || 0;

/** The family, in the order the bonuses add up. */
const PREDICATE_RULES: readonly PredicateRule[] = [
  { id: 'Witchbane', pen: true, factor: ({ target }) => when((target.psyRating || 0) > 0) },
  {
    id: 'Purifier',
    pen: true,
    factor: ({ target }) => when((target.daemonicProtection || 0) > 0 || !!target.fromBeyond),
  },
  { id: 'Scrapper', factor: ({ target }) => when((target.machineAP || 0) > 0) },
  { id: 'Requiem', factor: ({ target }) => when(!!target.undying) },
  { id: 'Titanbane', factor: ({ target }) => sizeStepsAboveAverage(target.size) },
  {
    id: 'Opportunist',
    factor: ({ target, runningWounds }) => when(maxWoundsOf(target) > 0 && runningWounds >= maxWoundsOf(target)),
  },
  {
    id: 'Headsman',
    factor: ({ target, runningWounds }) => when(maxWoundsOf(target) > 0 && runningWounds < maxWoundsOf(target) / 2),
  },
  { id: 'Butchering', factor: ({ target }) => when((target.criticalDamage?.length || 0) > 0) },
  {
    id: 'Cruel',
    factor: ({ target }) => when((target.conditions || []).some((c) => DISADVANTAGED.has(condType(c)))),
  },
  { id: 'Momentum', factor: ({ hitIndex }) => (hitIndex > 0 ? hitIndex : 0) },
];

/**
 * The family's whole arithmetic: damage and pen bonuses for this hit.
 * Each rule contributes only when its predicate holds; zeros otherwise.
 */
export function predicateBonuses(sp: string[], ctx: PredicateCtx): { dmg: number; pen: number } {
  let dmg = 0;
  let pen = 0;
  for (const rule of PREDICATE_RULES) {
    const x = getWSP(sp, rule.id);
    if (x <= 0) continue;
    const bonus = x * rule.factor(ctx);
    dmg += bonus;
    if (rule.pen) pen += bonus;
  }
  return { dmg, pen };
}
